<template>
  <div
    class="bg-gray-100"
    style="height: calc(100vh - 70px); width: 100%; min-width: 700px"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Create Campaign</h1>
            <p class="mt-2 text-sm text-gray-600">
              Create a new marketing campaign to reach your customers
            </p>
          </div>
          <button
            type="button"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            @click="goBack"
          >
            <svg
              class="-ml-1 mr-2 h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              ></path>
            </svg>
            Back to Campaigns
          </button>
        </div>
      </div>

      <!-- Two Column Layout -->
      <div
        class="Polaris-InlineGrid"
        style="
          --pc-inline-grid-grid-template-columns-xs: minmax(0, 1fr);
          --pc-inline-grid-grid-template-columns-md: minmax(0, 2fr) minmax(0, 1fr);
          --pc-inline-grid-gap-xs: var(--p-space-400);
        "
      >
        <!-- Left column: Configuration Form -->
        <div style="display: inherit; width: auto; border-inline-start: none">
          <div
            class="Polaris-InlineStack"
            style="
              --pc-inline-stack-align: center;
              --pc-inline-stack-wrap: wrap;
              --pc-inline-stack-gap-xs: var(--p-space-400);
              --pc-inline-stack-flex-direction-xs: row;
            "
          >
            <div
              style="color: var(--p-color-text-info-on-bg-fill); width: 100%"
            >
              <div class="Polaris-LegacyCard">
                <!-- Campaign Details Section -->
                <div class="Polaris-LegacyCard__Section">
                  <div
                    class="Polaris-LegacyStack Polaris-LegacyStack--alignmentCenter"
                  >
                    <div
                      class="Polaris-LegacyStack__Item Polaris-LegacyStack__Item--fill"
                    >
                      <h2 class="Polaris-Text--root Polaris-Text--headingMd">
                        Campaign Details
                      </h2>
                      <p class="mt-1 text-sm leading-5 text-gray-500">
                        Configure your campaign settings
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Campaign Name Section -->
                <div
                  class="Polaris-LegacyCard__Section border-b border-gray-200"
                >
                  <div
                    class="Polaris-BlockStack"
                    style="
                      --pc-block-stack-order: column;
                      --pc-block-stack-gap-xs: var(--p-space-400);
                    "
                  >
                    <div
                      class="Polaris-BlockStack"
                      style="
                        --pc-block-stack-order: column;
                        --pc-block-stack-gap-xs: var(--p-space-200);
                      "
                      role="group"
                    >
                      <div class="Polaris-FormLayout__Item">
                        <div class="Polaris-Labelled__LabelWrapper">
                          <div class="Polaris-Label">
                            <label class="Polaris-Label__Text"
                              >Campaign Name</label
                            >
                          </div>
                        </div>
                        <div class="Polaris-Connected">
                          <div
                            class="Polaris-Connected__Item Polaris-Connected__Item--primary"
                          >
                            <div class="Polaris-TextField">
                              <input
                                class="Polaris-TextField__Input"
                                type="text"
                                v-model="form.name"
                                ref="name"
                                placeholder="Flash sale campaign"
                              />
                              <div class="Polaris-TextField__Backdrop"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Customer Segment Section -->
                <div
                  class="Polaris-LegacyCard__Section border-b border-gray-200"
                >
                  <div
                    class="Polaris-BlockStack"
                    style="
                      --pc-block-stack-order: column;
                      --pc-block-stack-gap-xs: var(--p-space-400);
                    "
                  >
                    <div
                      class="Polaris-BlockStack"
                      style="
                        --pc-block-stack-order: column;
                        --pc-block-stack-gap-xs: var(--p-space-200);
                      "
                      role="group"
                    >
                      <div class="Polaris-FormLayout__Item">
                        <div class="Polaris-Labelled__LabelWrapper">
                          <div class="Polaris-Label">
                            <label class="Polaris-Label__Text"
                              >Customer Segment</label
                            >
                          </div>
                        </div>
                        <div class="Polaris-Connected">
                          <div
                            class="Polaris-Connected__Item Polaris-Connected__Item--primary"
                          >
                            <div class="Polaris-Select">
                              <select
                                class="Polaris-Select__Input"
                                v-model="form.segmentId"
                                aria-invalid="false"
                              >
                                <option value="">Select a segment</option>
                                <option
                                  v-for="(segment, index) in segments"
                                  :key="index"
                                  :value="segment.id"
                                >
                                  {{ segment.name }}
                                </option>
                              </select>
                              <div
                                class="Polaris-Select__Content"
                                aria-hidden="true"
                              >
                                <span class="Polaris-Select__SelectedOption">{{
                                  form.segmentId
                                    ? segments.find(
                                        (s) => s.id === form.segmentId
                                      )?.name
                                    : "Select a segment"
                                }}</span>
                                <span class="Polaris-Select__Icon">
                                  <span class="Polaris-Icon">
                                    <svg
                                      viewBox="0 0 20 20"
                                      class="Polaris-Icon__Svg"
                                      focusable="false"
                                      aria-hidden="true"
                                    >
                                      <path
                                        d="M10.884 4.323a1.25 1.25 0 0 0-1.768 0l-2.646 2.647a.75.75 0 0 0 1.06 1.06l2.47-2.47 2.47 2.47a.75.75 0 1 0 1.06-1.06l-2.646-2.647Z"
                                      ></path>
                                      <path
                                        d="m13.53 13.03-2.646 2.647a1.25 1.25 0 0 1-1.768 0l-2.646-2.647a.75.75 0 0 1 1.06-1.06l2.47 2.47 2.47-2.47a.75.75 0 0 1 1.06 1.06Z"
                                      ></path>
                                    </svg>
                                  </span>
                                </span>
                              </div>
                              <div class="Polaris-Select__Backdrop"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p class="mt-2 text-sm text-gray-500">
                        Create segments from your
                        <a
                          class="text-blue-700"
                          :href="segmentUrl"
                          target="_blank"
                          >Shopify admin > Customers > Segments</a
                        >
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Message Template Section -->
                <div
                  class="Polaris-LegacyCard__Section border-b border-gray-200"
                >
                  <div
                    class="Polaris-BlockStack"
                    style="
                      --pc-block-stack-order: column;
                      --pc-block-stack-gap-xs: var(--p-space-400);
                    "
                  >
                    <div
                      class="Polaris-BlockStack"
                      style="
                        --pc-block-stack-order: column;
                        --pc-block-stack-gap-xs: var(--p-space-200);
                      "
                      role="group"
                    >
                      <div class="Polaris-FormLayout__Item">
                        <div class="Polaris-Labelled__LabelWrapper">
                          <div class="Polaris-Label">
                            <label class="Polaris-Label__Text"
                              >Message Template</label
                            >
                          </div>
                        </div>
                        <div class="Polaris-Connected">
                          <div
                            class="Polaris-Connected__Item Polaris-Connected__Item--primary"
                          >
                            <div class="Polaris-TextField">
                              <textarea
                                class="Polaris-TextField__Input"
                                style="min-height: 120px; resize: vertical"
                                v-model="form.templateBody"
                                ref="content"
                                placeholder="Enter your campaign message here..."
                              ></textarea>
                              <div class="Polaris-TextField__Backdrop"></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Image Upload Section -->
                      <div class="Polaris-FormLayout__Item">
                        <div class="Polaris-Labelled__LabelWrapper">
                          <div class="Polaris-Label">
                            <label class="Polaris-Label__Text"
                              >Image (Optional)</label
                            >
                          </div>
                        </div>
                        <div class="Polaris-Connected">
                          <div
                            class="Polaris-Connected__Item Polaris-Connected__Item--primary"
                          >
                            <div class="Polaris-TextField">
                              <input
                                class="Polaris-TextField__Input"
                                type="file"
                                accept="image/*"
                                @change="uploadFile"
                                ref="imageInput"
                              />
                              <div class="Polaris-TextField__Backdrop"></div>
                            </div>
                          </div>
                        </div>
                        <p class="mt-2 text-sm text-gray-500">
                          Upload an image to include in your message (JPG, PNG,
                          GIF)
                        </p>
                        <!-- Image Preview -->
                        <div
                          v-if="form.imageFile || form.imageUrl"
                          class="mt-3"
                        >
                          <div class="relative inline-block">
                            <img
                              :src="form.imageUrl || form.imagePreview"
                              alt="Uploaded image"
                              class="w-32 h-24 object-cover rounded-lg border"
                              @error="handleImageError"
                            />
                            <button
                              type="button"
                              @click="removeImage"
                              style="top: 1px; right: -36px"
                              class="absolute bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Button Configuration Section -->
                      <div class="Polaris-FormLayout__Item">
                        <div class="Polaris-Labelled__LabelWrapper">
                          <div class="Polaris-Label">
                            <label class="Polaris-Label__Text"
                              >Call-to-Action Button (Optional)</label
                            >
                          </div>
                        </div>
                        <div class="Polaris-Connected">
                          <div
                            class="Polaris-Connected__Item Polaris-Connected__Item--primary"
                          >
                            <div class="Polaris-TextField">
                              <input
                                class="Polaris-TextField__Input"
                                :class="{
                                  'border-red-500': $v.form.buttonText.$error
                                }"
                                type="text"
                                v-model="form.buttonText"
                                @blur="$v.form.buttonText.$touch()"
                                placeholder="Button text (e.g., Shop Now, Learn More)"
                                maxlength="25"
                              />
                              <div class="Polaris-TextField__Backdrop"></div>
                            </div>
                          </div>
                        </div>
                        <div v-if="$v.form.buttonText.$error" class="mt-1">
                          <p
                            v-if="!$v.form.buttonText.maxLength"
                            class="text-sm text-red-600"
                          >
                            Button text must not exceed 25 characters
                          </p>
                          <p
                            v-if="!$v.form.buttonText.noEmoji"
                            class="text-sm text-red-600"
                          >
                            Button text can only contain letters, numbers, and
                            basic punctuation (no emojis)
                          </p>
                        </div>
                        <div class="mt-2">
                          <div class="Polaris-TextField">
                            <input
                              class="Polaris-TextField__Input"
                              type="url"
                              v-model="form.buttonUrl"
                              placeholder="Button URL (e.g., https://yourshop.com/product)"
                            />
                            <div class="Polaris-TextField__Backdrop"></div>
                          </div>
                        </div>
                        <p class="mt-2 text-sm text-gray-500">
                          Add a button with text and URL for call-to-action (max
                          25 characters, text and numbers only)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Scheduling Section -->
                <div
                  class="Polaris-LegacyCard__Section border-b border-gray-200"
                >
                  <div
                    class="Polaris-BlockStack"
                    style="
                      --pc-block-stack-order: column;
                      --pc-block-stack-gap-xs: var(--p-space-400);
                    "
                  >
                    <div
                      class="Polaris-BlockStack"
                      style="
                        --pc-block-stack-order: column;
                        --pc-block-stack-gap-xs: var(--p-space-200);
                      "
                      role="group"
                    >
                      <!-- Timezone Selection -->
                      <div class="Polaris-FormLayout__Item">
                        <div class="Polaris-Labelled__LabelWrapper">
                          <div class="Polaris-Label">
                            <label class="Polaris-Label__Text">Timezone</label>
                          </div>
                        </div>
                        <div class="Polaris-Connected">
                          <div
                            class="Polaris-Connected__Item Polaris-Connected__Item--primary"
                          >
                            <div class="Polaris-Select">
                              <select
                                id=":Rq6:"
                                class="Polaris-Select__Input"
                                aria-invalid="false"
                                v-model="form.timezone"
                                @input="changeScheduledAt"
                              >
                                <option value="">Select timezone</option>
                                <option
                                  v-for="(option, index) in timezoneOptions"
                                  :key="index"
                                  :value="option.value"
                                >
                                  {{ option.text }}
                                </option>
                              </select>
                              <div
                                class="Polaris-Select__Content"
                                aria-hidden="true"
                              >
                                <span class="Polaris-Select__SelectedOption">{{
                                  form.timezone || "Select timezone"
                                }}</span>
                                <span class="Polaris-Select__Icon">
                                  <span class="Polaris-Icon">
                                    <svg
                                      viewBox="0 0 20 20"
                                      class="Polaris-Icon__Svg"
                                      focusable="false"
                                      aria-hidden="true"
                                    >
                                      <path
                                        d="M10.884 4.323a1.25 1.25 0 0 0-1.768 0l-2.646 2.647a.75.75 0 0 0 1.06 1.06l2.47-2.47 2.47 2.47a.75.75 0 1 0 1.06-1.06l-2.646-2.647Z"
                                      ></path>
                                      <path
                                        d="m13.53 13.03-2.646 2.647a1.25 1.25 0 0 1-1.768 0l-2.646-2.647a.75.75 0 0 1 1.06-1.06l2.47 2.47 2.47-2.47a.75.75 0 0 1 1.06 1.06Z"
                                      ></path>
                                    </svg>
                                  </span>
                                </span>
                              </div>
                              <div class="Polaris-Select__Backdrop"></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Date and Time Selection -->
                      <div class="Polaris-FormLayout__Item">
                        <div class="Polaris-Labelled__LabelWrapper">
                          <div class="Polaris-Label">
                            <label class="Polaris-Label__Text"
                              >Schedule Date & Time</label
                            >
                          </div>
                        </div>
                        <div class="Polaris-Connected">
                          <div
                            class="Polaris-Connected__Item Polaris-Connected__Item--primary"
                          >
                            <div class="hide-daterange-selector">
                              <DateRangePicker
                                v-model="dateRangeConfig"
                                :minDate="minDate"
                                opens="inline"
                                :timePicker="true"
                                :singleDatePicker="true"
                                :ranges="false"
                                :autoApply="true"
                                @update="changeScheduledAt"
                              >
                              </DateRangePicker>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="Polaris-LegacyCard__Section">
                  <div class="flex justify-end space-x-4">
                    <button
                      type="button"
                      class="Polaris-Button Polaris-Button--pressable Polaris-Button--variantPrimary Polaris-Button--sizeMedium Polaris-Button--textAlignCenter"
                      @click="submitCampaign"
                      :disabled="
                        $v.form.$invalid ||
                        $v.form.buttonText.$error ||
                        buttonLoaders.isCreating
                      "
                    >
                      <span
                        class="Polaris-Text--root Polaris-Text--bodySm Polaris-Text--medium"
                      >
                        <span v-if="buttonLoaders.isCreating">Creating...</span>
                        <span v-else>Create Campaign</span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right column: Preview -->
        <div>
          <div class="Polaris-LegacyCard">
            <div class="Polaris-LegacyCard__Section">
              <div
                class="Polaris-LegacyStack Polaris-LegacyStack--alignmentCenter"
              >
                <div
                  class="Polaris-LegacyStack__Item Polaris-LegacyStack__Item--fill"
                >
                  <h2 class="Polaris-Text--root Polaris-Text--headingMd">
                    Campaign Preview
                  </h2>
                  <p class="mt-1 text-sm leading-5 text-gray-500">
                    Preview your campaign message
                  </p>
                </div>
              </div>
            </div>

            <div class="Polaris-LegacyCard__Section">
              <div class="">
                <div class="mb-4">
                  <div class="space-y-2 text-sm">
                    <div class="flex">
                      <span class="text-gray-600">Name:</span>
                      <span class="font-medium text-xs ml-2">{{
                        form.name || "Not set"
                      }}</span>
                    </div>
                    <div class="flex">
                      <span class="text-gray-600">Segment:</span>
                      <span class="font-medium text-xs ml-2">{{
                        getSegmentName(form.segmentId) || "Not selected"
                      }}</span>
                    </div>
                    <div class="flex">
                      <span class="text-gray-600">Timezone:</span>
                      <span class="font-medium text-xs ml-2">{{
                        getTimezoneName(form.timezone) || "Not selected"
                      }}</span>
                    </div>
                    <div class="flex">
                      <span class="text-gray-600">Scheduled:</span>
                      <span class="font-medium text-xs ml-2">{{
                        scheduledDateFormatted || "Not scheduled"
                      }}</span>
                    </div>
                  </div>
                </div>

                <div class="border-t pt-4">
                  <h3 class="text-lg font-medium text-gray-900 mb-2">
                    Message Preview
                  </h3>
                  <div
                    class="bg-gray-100 rounded-lg p-4 min-h-[400px] max-w-[300px] mx-auto"
                    style="background-color: #e5ddd5"
                  >
                    <div class="flex flex-col h-full">
                      <div class="flex-grow">
                        <!-- Image Preview -->

                        <!-- Message Preview -->
                        <div
                          v-if="form.templateBody"
                          class="bg-white rounded-lg p-3 text-sm inline-block max-w-[85%] shadow-sm"
                          style="border-radius: 8px 8px 8px 0"
                        >
                          <div
                            v-if="form.imageFile || form.imageUrl"
                            class="mb-3"
                          >
                            <img
                              :src="form.imagePreview || form.imageUrl"
                              alt="Campaign image"
                              class="w-full h-32 object-cover rounded-lg"
                              @error="handleImageError"
                              style="max-height: 120px"
                            />
                          </div>
                          <div class="whitespace-pre-wrap break-all">
                            {{ form.templateBody }}
                          </div>
                          <!-- Button Preview -->

                          <div
                            class="text-[11px] text-gray-500 text-right mt-1"
                          >
                            11:14 AM
                          </div>
                          <div
                            v-if="form.buttonText && form.buttonUrl"
                            class="mt-3 text-center border-t-2"
                          >
                            <a
                              :href="form.buttonUrl"
                              target="_blank"
                              class="inline-block text-blue-500 px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#075E54] transition-colors w-full text-center"
                            >
                              {{ form.buttonText }}
                            </a>
                          </div>
                        </div>
                        <div
                          v-else
                          class="bg-white rounded-lg p-3 text-sm inline-block max-w-[85%] text-gray-400 italic shadow-sm"
                          style="border-radius: 8px 8px 8px 0"
                        >
                          Your message will appear here...
                          <div
                            class="text-[11px] text-gray-500 text-right mt-1"
                          >
                            11:14 AM
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div class="flex items-start">
                    <svg
                      class="h-5 w-5 text-blue-400 mt-0.5 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                    <div class="text-sm text-blue-700">
                      <p class="font-medium">Campaign Status</p>
                      <p class="mt-1">
                        After creation, your campaign will be sent for approval
                        before it can be scheduled.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { required, maxLength } from "vuelidate/lib/validators";
import { format, zonedTimeToUtc } from "date-fns-tz";

import Timezone from "../../Mixins/timezones";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

export default {
  mixins: [Timezone],
  components: {
    DateRangePicker
  },

  data() {
    return {
      timezoneOptions: this.getTimezoneOptions(),

      name: "",
      body: "",

      form: {
        name: "",
        segmentId: "",
        templateBody: "",
        scheduledAt: "",
        timezone: "",
        imageUrl: "",
        buttonText: "",
        buttonUrl: "",
        imageFile: null,
        imagePreview: null,
        segmentName: ""
      },
      dateRangeConfig: {
        startDate: "",
        endDate: ""
      },
      minDate: "",
      campaign: null,

      showLimitWarning: false,
      showCSVError: false,
      buttonLoaders: {
        isDownloadingSegment: false,
        isCreating: false
      },
      totalPhoneNumbers: 0,
      scheduledDateFormatted: ""
    };
  },
  validations: {
    form: {
      name: {
        required
      },
      segmentId: {
        required
      },
      timezone: {
        required
      },
      templateBody: {
        required
      },
      scheduledAt: {
        required
      },
      buttonText: {
        maxLength: maxLength(25),
        noEmoji: function (value) {
          // If empty, it's valid (optional field)
          if (!value) return true;
          // Regex to match only alphanumeric characters, spaces, and common punctuation
          // This excludes emojis and special unicode characters
          const textOnlyRegex = /^[a-zA-Z0-9\s\-_.,!?()]+$/;
          return textOnlyRegex.test(value);
        }
      }
    }
  },
  computed: {
    segments() {
      const segments = this.$store.getters["Marketing/getSegments"];
      if (segments) {
        return segments;
      }
      return [];
    },
    segmentUrl() {
      const { shopOrigin } = this.$store.getters.getStore;
      return `https://${shopOrigin}/admin/customers/segments`;
    }
  },
  created() {
    let tomoorow = new Date();
    let today = new Date();
    today.setHours(0, 0, 0, 0);
    tomoorow.setDate(today.getDate() + 1);
    tomoorow.setHours(0, 0, 0, 0);
    this.minDate = tomoorow;
  },
  mounted() {
    this.$refs.name.focus();
    this.$store.dispatch("Marketing/getSegments");
  },
  beforeDestroy() {
    // Clean up object URL to prevent memory leaks
    if (this.form.imagePreview) {
      URL.revokeObjectURL(this.form.imagePreview);
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: "Campaigns" });
    },
    submitCampaign() {
      this.buttonLoaders.isCreating = true;

      // First upload image if present

      this.$store
        .dispatch("addTemplateForApproval", {
          requestParams: {
            type: "marketing_message",
            message: this.form.templateBody,
            imageUrl: this.form.imageUrl,
            buttonText: this.form.buttonText || null,
            buttonUrl: this.form.buttonUrl || null
          }
        })
        .then((response) => {
          const template = response.template;
          this.form.segmentName = this.getSegmentName(this.form.segmentId);
          this.$store
            .dispatch("Marketing/createCampaign", {
              requestParams: {
                ...this.form,
                templateApprovalId: template.id,
                scheduledAt: this.form.scheduledAt,
                imageUrl: this.form.imageUrl,
                buttonText: this.form.buttonText || null,
                buttonUrl: this.form.buttonUrl || null
              }
            })
            .then((response) => {
              this.campaign = response.campaign;
              this.buttonLoaders.isCreating = false;
              this.addMessageToCrisp();
              this.$router.push({ name: "Campaigns" });
            })
            .catch((error) => {
              this.buttonLoaders.isCreating = false;
              console.error("Error creating campaign:", error);
            });
        })
        .catch((error) => {
          this.buttonLoaders.isCreating = false;
          console.error("Error adding template for approval:", error);
        });
    },
    addMessageToCrisp() {
      let message = `Campaign Waiting for Approval : \n\n Name: ${this.form.name} \n\n When to send: ${this.scheduledDateFormatted}`;

      if (this.form.imageUrl) {
        message += `\n\n Image: ${this.form.imageUrl}`;
      }

      if (this.form.buttonText && this.form.buttonUrl) {
        message += `\n\n Button: ${this.form.buttonText} (${this.form.buttonUrl})`;
      }

      // eslint-disable-next-line
      $crisp.push(["do", "message:send", ["text", message]]);
    },

    getSegmentName(segmentId) {
      const segment = this.segments.find((s) => s.id === segmentId);
      return segment ? segment.name : "Unknown";
    },
    getTimezoneName(timezone) {
      const timezoneOption = this.timezoneOptions.find(
        (t) => t.value === timezone
      );
      return timezoneOption ? timezoneOption.text : "";
    },
    async uploadFile(event) {
      try {
        console.log({ event });
        this.isFileUploading = true;
        const file = event.target.files[0];

        // Check file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          alert("Image size should not be greater than 5 MB.");
          this.isFileUploading = false;
          // Reset the file input so user can select again
          if (this.$refs.imageInput) {
            this.$refs.imageInput.value = "";
          }
          return;
        }

        const response = await this.$store.dispatch("Marketing/getUploadUrl", {
          requestParams: { name: file.name, type: file.type }
        });

        const options = {
          headers: {
            "Content-Type": file.type
          }
        };
        await axios.put(response.url, file, options);
        this.form.imageUrl = response.imageUrl;
        this.isFileUploading = false;
      } catch (error) {
        this.isFileUploading = false;
        console.log("error while uploading file", error);
      }
    },
    changeScheduledAt() {
      try {
        // Target time zone
        const timeZone = this.form.timezone;
        const startDate = this.dateRangeConfig.startDate;
        // Pad month, date, hours and minutes with leading zeros
        const month = String(startDate.getMonth() + 1).padStart(2, "0");
        const date = String(startDate.getDate()).padStart(2, "0");
        const hours = String(startDate.getHours()).padStart(2, "0");
        const minutes = String(startDate.getMinutes()).padStart(2, "0");

        // Create date string in target timezone without converting
        const dateStr = `${startDate.getFullYear()}-${month}-${date}T${hours}:${minutes}:00`;

        // Create Date object and get timestamp
        const scheduledDate = new Date(dateStr);
        this.form.scheduledAt = scheduledDate.getTime() / 1000;

        // Format for display
        const formattedDate = format(
          scheduledDate,
          "yyyy-MM-dd HH:mm:ss zzzz",
          {
            timeZone
          }
        );

        // Convert to Unix timestamp in the target timezone using date-fns-tz
        const zonedDate = zonedTimeToUtc(scheduledDate, timeZone);
        this.form.scheduledAt = Math.floor(zonedDate.getTime() / 1000);

        console.log("scheduled date", formattedDate);
        console.log("scheduled date in unix", this.form.scheduledAt);
        this.scheduledDateFormatted = formattedDate;
      } catch (error) {
        console.log(error);
      }
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        const allowedTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif"
        ];
        if (!allowedTypes.includes(file.type)) {
          alert("Please select a valid image file (JPG, PNG, or GIF)");
          this.$refs.imageInput.value = "";
          return;
        }

        // Validate file size (5MB limit)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          alert("Image file size must be less than 5MB");
          this.$refs.imageInput.value = "";
          return;
        }

        this.form.imageFile = file;
        this.form.imagePreview = URL.createObjectURL(file);
        this.form.imageUrl = null; // Clear previous URL if any
      }
    },
    removeImage() {
      if (this.form.imagePreview) {
        URL.revokeObjectURL(this.form.imagePreview);
      }
      this.form.imageFile = null;
      this.form.imagePreview = null;
      this.form.imageUrl = null;
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = "";
      }
    },
    handleImageError(event) {
      event.target.src = "https://via.placeholder.com/150"; // Fallback image
    }
  }
};
</script>

<style>
.hide-daterange-selector .form-control.reportrange-text {
  display: none;
}
</style>
